import { memo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Card, Tag, Typography, Space, Badge } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  PlusSquareOutlined,
  MinusSquareOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getNodeStatusColor } from '../../utils/graphLayout';

const { Text } = Typography;

interface JobNodeData {
  label: string;
  status: string;
  time: string;
  endTime?: string;
  namespace: string;
  jobId: string;
  spanId?: string;
  traceId: string;
  details?: string;
  hasChildren?: boolean;
  isExpanded?: boolean;
  childCount?: number;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  JOB_START: <PlayCircleOutlined />,
  JOB_END: <CheckCircleOutlined />,
  JOB_END_EXCEPTION: <CloseCircleOutlined />,
};

function JobNodeComponent({ data, selected }: NodeProps<JobNodeData>) {
  const borderColor = getNodeStatusColor(data.status);
  const icon = STATUS_ICONS[data.status] || <LoadingOutlined />;
  const hasChildren = data.hasChildren;
  const isExpanded = data.isExpanded;
  const childCount = data.childCount || 0;

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <Card
        size="small"
        style={{
          width: 200,
          borderColor: selected ? '#1890ff' : borderColor,
          borderWidth: 2,
          boxShadow: selected ? '0 0 0 2px rgba(24, 144, 255, 0.2)' : undefined,
          cursor: 'pointer',
        }}
        styles={{ body: { padding: 12 } }}
      >
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Space>
              <span style={{ color: borderColor }}>{icon}</span>
              <Text strong style={{ fontSize: 13 }}>
                Job
              </Text>
            </Space>
            {hasChildren && (
              <Badge count={childCount} size="small" style={{ backgroundColor: '#8c8c8c' }}>
                {isExpanded ? (
                  <MinusSquareOutlined style={{ fontSize: 14, color: '#1890ff' }} />
                ) : (
                  <PlusSquareOutlined style={{ fontSize: 14, color: '#1890ff' }} />
                )}
              </Badge>
            )}
          </Space>

          <Tag color={borderColor} style={{ margin: 0 }}>
            {data.status.replace(/_/g, ' ')}
          </Tag>

          <Text
            type="secondary"
            style={{ fontSize: 11 }}
            ellipsis={{ tooltip: data.jobId }}
          >
            {data.jobId.substring(0, 12)}...
          </Text>

          <Text type="secondary" style={{ fontSize: 10 }}>
            {dayjs(data.time).format('HH:mm:ss')}
            {data.endTime && ` → ${dayjs(data.endTime).format('HH:mm:ss')}`}
          </Text>
        </Space>
      </Card>
      <Handle type="source" position={Position.Bottom} />
    </>
  );
}

export const JobNode = memo(JobNodeComponent);
